import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from '@mui/material'
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useSnackbar } from 'notistack'
import { PageHeader } from '../components/ui'

interface StockMovement {
  id: string
  product_name: string
  variant_name: string
  variant_sku: string
  movement_type: string
  quantity: number
  stock_before: number | null
  stock_after: number | null
  reference_type: string | null
  reference_id: string | null
  warehouse_name: string | null
  created_by: string
  created_at: string
  notes: string | null
}

const StockMovementHistory: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [movements, setMovements] = useState<StockMovement[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>('all')

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    loadMovements()
  }, [page, pageSize, startDate, endDate, movementTypeFilter])

  const loadMovements = async () => {
    setIsLoading(true)
    try {
      const token = localStorage.getItem('token')
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: (page * pageSize).toString()
      })

      if (startDate) {
        params.append('start_date', startDate.toISOString())
      }

      if (endDate) {
        params.append('end_date', endDate.toISOString())
      }

      const response = await fetch(`/api/admin/inventory/movements?${params}`, {
        headers
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      console.log('Stock Movements Data:', data)

      let filteredMovements = data || []

      // Apply client-side filters
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        filteredMovements = filteredMovements.filter((mov: StockMovement) =>
          mov.product_name?.toLowerCase().includes(searchLower) ||
          mov.variant_sku?.toLowerCase().includes(searchLower) ||
          mov.variant_name?.toLowerCase().includes(searchLower)
        )
      }

      if (movementTypeFilter && movementTypeFilter !== 'all') {
        filteredMovements = filteredMovements.filter((mov: StockMovement) =>
          mov.movement_type === movementTypeFilter
        )
      }

      setMovements(filteredMovements)
      setTotalRows(filteredMovements.length)
    } catch (error) {
      console.error('Failed to load stock movements:', error)
      enqueueSnackbar('Failed to load stock movements', { variant: 'error' })
      setMovements([])
      setTotalRows(0)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      enqueueSnackbar('Export functionality coming soon', { variant: 'info' })
    } catch (error) {
      enqueueSnackbar('Failed to export movements', { variant: 'error' })
    }
  }

  const getMovementIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'purchase':
      case 'found':
      case 'return':
        return <TrendingUpIcon fontSize="small" color="success" />
      case 'sale':
      case 'damage':
      case 'loss':
      case 'theft':
        return <TrendingDownIcon fontSize="small" color="error" />
      default:
        return <SwapHorizIcon fontSize="small" color="action" />
    }
  }

  const getMovementColor = (type: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (type?.toLowerCase()) {
      case 'purchase':
      case 'found':
      case 'return':
        return 'success'
      case 'sale':
        return 'info'
      case 'damage':
      case 'loss':
      case 'theft':
        return 'error'
      case 'adjustment':
      case 'correction':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <PageHeader
          title="Stock Movement History"
          subtitle="Complete history of all stock movements and transactions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Inventory', path: '/inventory' },
            { label: 'Movement History' },
          ]}
          actions={[
            {
              label: 'Refresh',
              onClick: loadMovements,
              variant: 'outlined',
              color: 'primary',
              icon: <RefreshIcon />,
            },
            {
              label: 'Export',
              onClick: handleExport,
              variant: 'contained',
              color: 'secondary',
              icon: <DownloadIcon />,
            },
          ]}
        >
          {/* Filters */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="Search by product, SKU, or variant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  minWidth: 300,
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                select
                label="Movement Type"
                value={movementTypeFilter}
                onChange={(e) => setMovementTypeFilter(e.target.value)}
                size="small"
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="purchase">Purchase</MenuItem>
                <MenuItem value="sale">Sale</MenuItem>
                <MenuItem value="adjustment">Adjustment</MenuItem>
                <MenuItem value="return">Return</MenuItem>
                <MenuItem value="damage">Damage</MenuItem>
                <MenuItem value="loss">Loss</MenuItem>
                <MenuItem value="theft">Theft</MenuItem>
                <MenuItem value="found">Found</MenuItem>
                <MenuItem value="correction">Correction</MenuItem>
              </TextField>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      minWidth: 140,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      minWidth: 140,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }
                  }
                }}
              />
            </Stack>
          </Card>
        </PageHeader>

        {/* Loading indicator */}
        {isLoading && <LinearProgress />}

        {/* Data Table - Full Screen */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden', px: 3, pb: 3 }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Date & Time
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Product Details
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Movement Type
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Quantity
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Stock Before
                    </TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Stock After
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Reference
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Warehouse
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 600 }}>
                      Created By
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.length === 0 && !isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No stock movements found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    movements.map((movement, index) => (
                      <TableRow
                        key={movement.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          },
                          backgroundColor: index % 2 === 0 ? 'white' : 'rgba(0, 0, 0, 0.02)',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(movement.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(movement.created_at).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {movement.product_name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {movement.variant_name || 'N/A'} • {movement.variant_sku || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {getMovementIcon(movement.movement_type)}
                            <Chip
                              label={movement.movement_type?.toUpperCase() || 'UNKNOWN'}
                              color={getMovementColor(movement.movement_type)}
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={movement.quantity > 0 ? 'success.main' : 'error.main'}
                          >
                            {movement.quantity > 0 ? '+' : ''}{movement.quantity || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {movement.stock_before !== null ? movement.stock_before : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {movement.stock_after !== null ? movement.stock_after : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {movement.reference_type ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {movement.reference_type}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {movement.reference_id || '-'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {movement.warehouse_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {movement.created_by || 'System'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[20, 50, 100, 200]}
              component="div"
              count={totalRows}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10))
                setPage(0)
              }}
              sx={{
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              }}
            />
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}

export default StockMovementHistory
